import {
  DuplicateReadingError,
  type DeepReadSession,
  type Reading,
  type ReadingStore,
  type Rebuild,
  type RebuildMode,
} from "./types";

/**
 * The same ReadingStore contract, backed by the account instead of the
 * browser.
 *
 * It implements the interface the app already talks to, so signing in
 * changes where a reading lives without changing a single call site. The
 * IndexedDB store stays in the codebase and stays in use: it is what an
 * anonymous visitor's Quick read runs on.
 */
export class ServerReadingStore implements ReadingStore {
  async addReading(reading: Reading): Promise<void> {
    const body = {
      id: reading.id,
      title: reading.title,
      sourceText: reading.sourceText,
      createdAt: reading.createdAt,
      pages: reading.pages,
      mode: reading.mode,
      pdfBase64: reading.pdfBlob
        ? await blobToBase64(reading.pdfBlob)
        : undefined,
    };
    const res = await fetch("/api/readings", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      const dupe = (await res.json().catch(() => ({}))) as {
        error?: string;
        readingId?: string;
        title?: string;
      };
      throw new DuplicateReadingError(
        dupe.error ?? "You already have this reading.",
        dupe.readingId ?? "",
        dupe.title ?? "",
      );
    }
    if (!res.ok) {
      const detail = await res
        .json()
        .then((b: { error?: string }) => b.error)
        .catch(() => null);
      throw new Error(detail ?? `Request failed (${res.status})`);
    }
  }

  /**
   * The library list.
   *
   * sourceText comes back empty and pdfBlob absent: the list endpoint does
   * not send them, because rendering a list of titles should not mean
   * downloading every PDF the student owns. Anything that needs the body of
   * a reading calls getReading.
   */
  async listReadings(): Promise<Reading[]> {
    const data = await send<{
      readings: {
        id: string;
        title: string;
        createdAt: number;
        pageCount: number;
        courseId?: string | null;
        hasPdf?: boolean;
        mode?: RebuildMode;
        rebuilds?: RebuildMode[];
      }[];
    }>("/api/readings");

    return data.readings.map((row) => ({
      id: row.id,
      title: row.title,
      createdAt: row.createdAt,
      sourceText: "",
      courseId: row.courseId ?? null,
      hasPdf: row.hasPdf ?? false,
      mode: row.mode,
      rebuilds: row.rebuilds,
    }));
  }

  async getReading(id: string): Promise<Reading | undefined> {
    const data = await send<{
      id: string;
      title: string;
      sourceText: string;
      createdAt: number;
      pages?: { pageNumber: number; text: string }[];
      hasPdf?: boolean;
      mode?: RebuildMode;
      pdfBase64?: string;
      pdfContentType?: string;
    } | null>(`/api/readings/${encodeURIComponent(id)}?pdf=0`, {
      allow404: true,
    });

    if (!data) return undefined;

    // The file comes separately, as bytes. Older servers that ignore ?pdf=0
    // still send base64, and that is honoured too.
    let pdfBlob: Blob | undefined;
    if (data.pdfBase64) {
      pdfBlob = base64ToBlob(
        data.pdfBase64,
        data.pdfContentType ?? "application/pdf",
      );
    } else if (data.hasPdf) {
      const res = await fetch(`/api/readings/${encodeURIComponent(id)}/pdf`, {
        credentials: "same-origin",
      });
      // A reading whose file cannot be fetched still opens; only the views of
      // the original are missing.
      if (res.ok)
        pdfBlob = new Blob([await res.arrayBuffer()], {
          type: "application/pdf",
        });
    }

    return {
      id: data.id,
      title: data.title,
      sourceText: data.sourceText,
      createdAt: data.createdAt,
      pages: data.pages,
      mode: data.mode,
      pdfBlob,
    };
  }

  async renameReading(id: string, title: string): Promise<void> {
    await send(`/api/readings/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    });
  }

  async deleteReading(id: string): Promise<void> {
    await send(`/api/readings/${encodeURIComponent(id)}`, { method: "DELETE" });
  }

  async saveRebuild(rebuild: Rebuild): Promise<void> {
    await send(
      `/api/readings/${encodeURIComponent(rebuild.readingId)}/rebuild`,
      {
        method: "PUT",
        body: JSON.stringify(rebuild),
      },
    );
  }

  async getRebuild(
    readingId: string,
    mode: RebuildMode = "quick",
  ): Promise<Rebuild | undefined> {
    const data = await send<{ rebuild: Rebuild | null } | null>(
      `/api/readings/${encodeURIComponent(readingId)}/rebuild?mode=${mode}`,
      { allow404: true },
    );
    return data?.rebuild ?? undefined;
  }

  async saveDeepReadSession(session: DeepReadSession): Promise<void> {
    // Sketch images are the one thing the session document cannot carry over
    // the wire; they are stripped here rather than silently rejected by the
    // schema, and the notes they belong to are kept.
    const { sketches, ...rest } = session;
    const body = {
      ...rest,
      sketches: sketches.map(({ segmentId, note }) => ({ segmentId, note })),
    };
    await send("/api/deep-sessions", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async getDeepReadSession(id: string): Promise<DeepReadSession | undefined> {
    const data = await send<{ session: DeepReadSession | null } | null>(
      `/api/deep-sessions?id=${encodeURIComponent(id)}`,
      { allow404: true },
    );
    return data?.session ?? undefined;
  }

  async listDeepReadSessionsForReading(
    readingId: string,
  ): Promise<DeepReadSession[]> {
    const data = await send<{ sessions: DeepReadSession[] }>(
      `/api/deep-sessions?readingId=${encodeURIComponent(readingId)}`,
    );
    return data.sessions;
  }
}

async function send<T = void>(
  url: string,
  init: RequestInit & { allow404?: boolean } = {},
): Promise<T> {
  const { allow404, ...rest } = init;
  const response = await fetch(url, {
    ...rest,
    headers: rest.body ? { "Content-Type": "application/json" } : undefined,
    // The session cookie is what identifies the account, and these are
    // same-origin, so the default is already right; stated anyway because
    // getting it wrong fails as "empty library", not as an error.
    credentials: "same-origin",
  });

  if (response.status === 404 && allow404) return null as T;

  if (!response.ok) {
    const detail = await response
      .json()
      .then((body: { error?: string }) => body.error)
      .catch(() => null);
    throw new Error(detail ?? `Request failed (${response.status})`);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // Chunked: String.fromCharCode(...bytes) on a multi-megabyte PDF blows the
  // argument limit and throws before it ever reaches btoa.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}
